import React from "react";
import { PluginStore } from "graylog-web-plugin/plugin";
import moment from "moment";
import "moment-duration-format";
import { defaultCompare as naturalSort } from "logic/DefaultCompare";
import usePluginEntities from "hooks/usePluginEntities";
import usePluggableLicenseCheck from "hooks/usePluggableLicenseCheck";
import { Alert } from "components/bootstrap";
import { isPermitted } from "util/PermissionsMixin";
import { MarkdownPreview } from "components/common/MarkdownEditor";
import commonStyles from "../common/commonStyles.css";
const getPlugin = (name, type) => {
  if (type === void 0) {
    return void 0;
  }
  return PluginStore.exports(name).find((edt) => edt.type === type);
};
export function useEventProcedureSummaryComponents({
  eventProcedureId = void 0,
  remediationSteps = void 0
}) {
  const pluggableEventProcedureSummary = usePluginEntities("views.components.eventProcedureSummary");
  const {
    data: { valid: validSecurityLicense }
  } = usePluggableLicenseCheck("/license/security");
  let label = null;
  let Component = null;
  if (validSecurityLicense) {
    label = "Event Procedure Summary";
    if (eventProcedureId) {
      const first = pluggableEventProcedureSummary == null ? void 0 : pluggableEventProcedureSummary[0];
      const PluggableEventProcedureSummary = first == null ? void 0 : first.component;
      Component = PluggableEventProcedureSummary ? /* @__PURE__ */ React.createElement(PluggableEventProcedureSummary, { eventProcedureId, key: first == null ? void 0 : first.key }) : /* @__PURE__ */ React.createElement("p", null, "Event Procedure Summary component is not available.");
    } else {
      Component = /* @__PURE__ */ React.createElement("p", null, "This Event does not have any Event Procedures.");
    }
  } else {
    label = "Remediation Steps";
    Component = /* @__PURE__ */ React.createElement(
      MarkdownPreview,
      {
        show: true,
        withFullView: true,
        noBorder: true,
        noBackground: true,
        value: remediationSteps || "No remediation steps given"
      }
    );
  }
  return {
    label,
    Component
  };
}
export function renderCondition(eventDefinition, definitionId, currentUser) {
  const { config, _scope } = eventDefinition;
  const conditionPlugin = getPlugin("eventDefinitionTypes", config.type);
  const component = (conditionPlugin == null ? void 0 : conditionPlugin.summaryComponent) ? React.createElement(conditionPlugin.summaryComponent, {
    config,
    currentUser,
    definitionId,
    entityScope: _scope
  }) : /* @__PURE__ */ React.createElement("p", null, "Condition plugin ", /* @__PURE__ */ React.createElement("em", null, config.type), " does not provide a summary.");
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("h3", { className: commonStyles.title }, (conditionPlugin == null ? void 0 : conditionPlugin.displayName) || config.type), component);
}
export function renderField(fieldName, config, keys, currentUser) {
  if (!config.providers || config.providers.length === 0) {
    return /* @__PURE__ */ React.createElement("span", { key: fieldName }, "No field value provider configured.");
  }
  const provider = config.providers[0];
  const fieldProviderPlugin = getPlugin("fieldValueProviders", provider == null ? void 0 : provider.type);
  return (fieldProviderPlugin == null ? void 0 : fieldProviderPlugin.summaryComponent) ? React.createElement(fieldProviderPlugin.summaryComponent, {
    fieldName,
    config,
    keys,
    key: fieldName,
    currentUser
  }) : /* @__PURE__ */ React.createElement("p", { key: fieldName }, "Provider plugin ", /* @__PURE__ */ React.createElement("em", null, provider.type), " does not provide a summary.");
}
function renderFieldList(fieldNames, fields, keys, currentUser) {
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("dl", null, /* @__PURE__ */ React.createElement("dt", null, "Keys"), /* @__PURE__ */ React.createElement("dd", null, keys.length > 0 ? keys.join(", ") : "No Keys configured for Events based on this Definition.")), fieldNames.sort(naturalSort).map((fieldName) => renderField(fieldName, fields[fieldName], keys, currentUser)));
}
export function renderFields(fields, keys, currentUser) {
  const fieldNames = Object.keys(fields);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("h3", { className: commonStyles.title }, "Fields"), fieldNames.length === 0 ? /* @__PURE__ */ React.createElement("p", null, "No Fields configured for Events based on this Definition.") : renderFieldList(fieldNames, fields, keys, currentUser));
}
function renderNotification(definitionNotification, notifications) {
  const notification = notifications.find((n) => n.id === definitionNotification.notification_id);
  let content = null;
  if (notification) {
    const notificationPlugin = getPlugin("eventNotificationTypes", notification.config.type);
    content = notificationPlugin.summaryComponent ? React.createElement(notificationPlugin.summaryComponent, {
      type: notificationPlugin.displayName,
      notification,
      definitionNotification
    }) : /* @__PURE__ */ React.createElement("p", null, "Notification plugin ", /* @__PURE__ */ React.createElement("em", null, notification.config.type), " does not provide a summary.");
  } else {
    content = /* @__PURE__ */ React.createElement("p", null, "Could not find information for Notification ", /* @__PURE__ */ React.createElement("em", null, definitionNotification.notification_id), ".");
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, { key: definitionNotification.notification_id }, content);
}
function renderNotificationSettings(notificationSettings) {
  const formattedDuration = moment.duration(notificationSettings.grace_period_ms).format("d [days] h [hours] m [minutes] s [seconds]", { trim: "all" });
  const formattedGracePeriod = notificationSettings.grace_period_ms ? `Grace Period is set to ${formattedDuration}` : "Grace Period is disabled";
  const formattedBacklogSize = notificationSettings.backlog_size ? `Notifications will include ${notificationSettings.backlog_size} messages` : "Notifications will not include any messages.";
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("h4", null, "Settings"), /* @__PURE__ */ React.createElement("dl", null, /* @__PURE__ */ React.createElement("dd", null, formattedGracePeriod), /* @__PURE__ */ React.createElement("dd", null, formattedBacklogSize)));
}
export function renderNotifications(definitionNotifications, notificationSettings, notifications, currentUser) {
  const effectiveDefinitionNotifications = definitionNotifications.filter(
    (n) => isPermitted(currentUser.permissions, `eventnotifications:read:${n.notification_id}`)
  );
  const notificationsWithMissingPermissions = definitionNotifications.filter(
    (n) => !effectiveDefinitionNotifications.map((nObj) => nObj.notification_id).includes(n.notification_id)
  );
  const warning = notificationsWithMissingPermissions.length > 0 ? /* @__PURE__ */ React.createElement(Alert, { bsStyle: "warning" }, "Missing Notifications Permissions for:", /* @__PURE__ */ React.createElement("br", null), notificationsWithMissingPermissions.map((n) => n.notification_id).join(", ")) : null;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("h3", { className: commonStyles.title }, "Notifications"), /* @__PURE__ */ React.createElement("span", null, warning), effectiveDefinitionNotifications.length === 0 && notificationsWithMissingPermissions.length <= 0 ? /* @__PURE__ */ React.createElement("p", null, "This Event is not configured to trigger any Notifications.") : /* @__PURE__ */ React.createElement(React.Fragment, null, renderNotificationSettings(notificationSettings), definitionNotifications.map((notification) => renderNotification(notification, notifications))));
}
export function useTechniquesSummary(eventDefinition) {
  var _a, _b;
  const tacticsTechniquesSummaryPlugin = usePluginEntities("eventDefinitions.components.tacticsTechniquesSummary")[0];
  const tacticsTechniquesSummaryEnabled = (_b = (_a = tacticsTechniquesSummaryPlugin == null ? void 0 : tacticsTechniquesSummaryPlugin.useCondition) == null ? void 0 : _a.call(tacticsTechniquesSummaryPlugin)) != null ? _b : !!tacticsTechniquesSummaryPlugin;
  return tacticsTechniquesSummaryEnabled && tacticsTechniquesSummaryPlugin ? /* @__PURE__ */ React.createElement(tacticsTechniquesSummaryPlugin.component, { eventDefinition }) : null;
}
